import React from "react";
import { PanFormValues } from "./panFormFields";

type Props = {
  values: PanFormValues;
  setFields: (patch: Partial<PanFormValues>) => void;
  /**
   * Which signature to adjust: "crossSign" (across the left photo, the
   * default) or "sig" (the Verification & Declaration box).
   */
  target?: "crossSign" | "sig";
};

/** Move step, in % of the page — about 1 mm on A4. */
const MOVE_STEP = 0.5;
const SIZE_STEP = 10;
const SIZE_MIN = 40;
const SIZE_MAX = 250;
const TILT_STEP = 3;

const num = (v: string, fallback: number) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};
/** Rounded so repeated steps do not print 0.49999999 into the stored formData. */
const round = (n: number) => String(Math.round(n * 100) / 100);

const btn: React.CSSProperties = {
  minWidth: "26px",
  height: "24px",
  padding: "0 6px",
  border: "1px solid #bbb",
  borderRadius: "4px",
  background: "#fff",
  fontSize: "12px",
  lineHeight: "22px",
  cursor: "pointer",
};

/**
 * Nudges the cross sign on the left photo: move, resize, tilt, reset. The
 * default placement fits most signatures, but a long or very short one needs
 * shifting to sit across the photo's edge the way a hand-signed one does.
 */
const CrossSignControls = ({ values, setFields, target = "crossSign" }: Props) => {
  const kDx = `${target}Dx` as const;
  const kDy = `${target}Dy` as const;
  const kSize = `${target}Size` as const;
  const kTilt = `${target}Tilt` as const;
  const dx = num(values[kDx], 0);
  const dy = num(values[kDy], 0);
  const size = num(values[kSize], 100);
  const tilt = num(values[kTilt], 0);

  const move = (x: number, y: number) =>
    setFields({ [kDx]: round(dx + x), [kDy]: round(dy + y) } as Partial<PanFormValues>);
  const resize = (step: number) =>
    setFields({
      [kSize]: round(Math.min(SIZE_MAX, Math.max(SIZE_MIN, size + step))),
    } as Partial<PanFormValues>);
  const turn = (step: number) => setFields({ [kTilt]: round(tilt + step) } as Partial<PanFormValues>);
  const reset = () =>
    setFields({ [kDx]: "0", [kDy]: "0", [kSize]: "100", [kTilt]: "0" } as Partial<PanFormValues>);

  const group = (label: string, children: React.ReactNode) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
      <span style={{ color: "#555" }}>{label}</span>
      {children}
    </span>
  );

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px 14px",
        fontSize: "12px",
      }}
    >
      {group(
        "Move",
        <>
          <button type="button" style={btn} title="Left" onClick={() => move(-MOVE_STEP, 0)}>←</button>
          <button type="button" style={btn} title="Right" onClick={() => move(MOVE_STEP, 0)}>→</button>
          <button type="button" style={btn} title="Up" onClick={() => move(0, -MOVE_STEP)}>↑</button>
          <button type="button" style={btn} title="Down" onClick={() => move(0, MOVE_STEP)}>↓</button>
        </>
      )}
      {group(
        "Size",
        <>
          <button type="button" style={btn} title="Smaller" onClick={() => resize(-SIZE_STEP)}>−</button>
          <span style={{ minWidth: "34px", textAlign: "center" }}>{Math.round(size)}%</span>
          <button type="button" style={btn} title="Bigger" onClick={() => resize(SIZE_STEP)}>+</button>
        </>
      )}
      {group(
        "Tilt",
        <>
          <button type="button" style={btn} title="Tilt up" onClick={() => turn(-TILT_STEP)}>↺</button>
          <button type="button" style={btn} title="Tilt down" onClick={() => turn(TILT_STEP)}>↻</button>
        </>
      )}
      <button type="button" style={btn} onClick={reset}>Reset</button>
    </span>
  );
};

export default CrossSignControls;
